<script>
import { computed, nextTick, watch, onScopeDispose } from '@vue/composition-api'
import Vue from 'vue'

import AppToast from '@/components/ui/AppToast.vue'
import {
    DISPATCHER_LISTS_VIEW_PERMISSION,
    FOREMAN_VIEW_PERMISSION,
    ORDER_DEPARTMENT_VIEW_PERMISSION,
} from '@/constants/permissions'
import { getUserData } from '@/core/auth/utils'
import OrderChangesToast from '@/core/components/toastification/OrderChangesToast.vue'
import { useOrderChangeNotifications } from '@/modules/dispatcher/composables/useOrderChangeNotifications'
import router from '@/router'
import store from '@/store'
import { showCustomNotification } from '@/utils/notifications'

export default {
    name: 'ManagerNotification',

    setup() {
        const userData = getUserData()
        const isMobile = computed(() => store.getters['app/isMobile'])
        const managerNotifications = computed(
            () => store.state.orders.managerNotifications
        )
        const currentPreviewOrderId = computed(
            () => store.state.orders.previewOrderId
        )

        // Проверка разрешений
        const hasPermission = permission =>
            userData.permissions.includes(permission)

        const hasForemanAccess = computed(() =>
            hasPermission(FOREMAN_VIEW_PERMISSION)
        )

        const hasDepartmentAccess = computed(() =>
            hasPermission(ORDER_DEPARTMENT_VIEW_PERMISSION)
        )

        const hasDispatcherAccess = computed(() =>
            hasPermission(DISPATCHER_LISTS_VIEW_PERMISSION)
        )

        /**
         * Открывает заказ в режиме предпросмотра
         */
        const togglePreviewOrder = orderId => {
            if (orderId === currentPreviewOrderId.value) {
                store.commit('orders/SET_PREVIEW_ORDER_ID', null)
            }
            nextTick(() => {
                store.commit('orders/SET_PREVIEW_ORDER_ID', orderId)
            })
        }

        /**
         * Обработчик открытия заказа для Формана
         */
        const handleOpenForemanOrder = orderId => {
            togglePreviewOrder(orderId)

            if (router.currentRoute.path !== '/foreman') {
                router.push('/foreman')
            }
        }

        /**
         * Обработчик открытия заказа для Отдела заказов
         */
        const handleOpenDepartmentOrder = orderId => {
            togglePreviewOrder(orderId)

            if (router.currentRoute.path !== '/orders-department') {
                router.push('/orders-department')
            }
        }

        /**
         * Обработчик открытия заказа для Диспетчера
         */
        const handleOpenDispatcherOrder = orderId => {
            if (!hasDispatcherAccess.value) return

            // Если уже на странице этого же заказа — сбрасываем и перезагружаем данные
            if (
                router.currentRoute.name === 'dispatcher-order-details' &&
                router.currentRoute.params.id === orderId
            ) {
                store.commit('dispatcher/RESET_CURRENT_ORDER')
                store.dispatch('dispatcher/fetchOrderById', orderId)
                return
            }

            router.push({
                name: 'dispatcher-order-details',
                params: { id: orderId },
            })
        }

        /**
         * Определяет обработчик открытия заказа на основе разрешений
         * Приоритет: диспетчер > отдел > форман
         */
        const getOpenOrderHandler = () => {
            if (hasDispatcherAccess.value) return handleOpenDispatcherOrder
            if (hasDepartmentAccess.value) return handleOpenDepartmentOrder
            if (hasForemanAccess.value) return handleOpenForemanOrder
            return null
        }

        /**
         * Показать уведомление об отмене заказа
         */
        const showCanceledNotification = value => {
            if (!value?.notificationText) return

            showCustomNotification({
                component: AppToast,
                props: {
                    variant: 'error',
                    text: value.notificationText,
                },
                options: {
                    position: isMobile.value ? 'top-center' : 'top-right',
                    timeout: false,
                    closeOnClick: false,
                    toastClassName: 'order-cancel-notification',
                },
            })
        }

        /**
         * Показать уведомление об изменении заказа
         */
        const showChangedNotification = value => {
            if (!value?.orderId || !value?.notificationText) return

            const handler = getOpenOrderHandler()
            if (!handler) {
                console.warn(
                    '[ManagerNotification] No handler found for changed order notification'
                )
                return
            }

            showCustomNotification({
                component: OrderChangesToast,
                props: {
                    orderId: value.orderId,
                    changes: value.notificationText,
                },
                listeners: {
                    'open-order': handler,
                },
                options: {
                    position: isMobile.value ? 'top-center' : 'top-right',
                    timeout: false,
                    closeOnClick: false,
                    toastClassName: 'order-change-notification',
                },
            })
        }

        /**
         * Показать уведомление для диспетчера
         */
        const { show: showDispatcherNotification } =
            useOrderChangeNotifications(
                Vue.prototype.$toast,
                OrderChangesToast,
                handleOpenDispatcherOrder
            )

        // Подписки на уведомления (без immediate, чтобы избежать дублирования при монтировании)
        const unwatchCanceled = watch(
            () => managerNotifications.value.canceledOrderNotification,
            (newValue, oldValue) => {
                if (newValue && newValue !== oldValue) {
                    showCanceledNotification(newValue)
                    // Сбрасываем значение в хранилище после показа
                    store.commit('orders/SET_CANCELED_ORDER_NOTIFICATION', null)
                }
            }
        )

        // EDS-5111 (вариант А): на экранах диспетчера уведомления об изменении
        // заказа приходят по Centrifugo (showDispatcherNotification). Legacy WS
        // order_updated дублировал бы их — гасим его на диспетчере, оставляя
        // для цеха/отдела заказов.
        const dispatcherRoutes = ['dispatcher', 'dispatcher-order-details']

        const unwatchChanged = watch(
            () => managerNotifications.value.changedOrderNotification,
            (newValue, oldValue) => {
                if (newValue && newValue !== oldValue) {
                    if (!dispatcherRoutes.includes(router.currentRoute.name)) {
                        showChangedNotification(newValue)
                    }
                    store.commit('orders/SET_CHANGED_ORDER_NOTIFICATION', null)
                }
            }
        )

        const unwatchDispatcher = watch(
            () =>
                managerNotifications.value.dispatcherOrderNotifications
                    ?.length ?? 0,
            () => {
                const queue = [
                    ...(managerNotifications.value
                        .dispatcherOrderNotifications || []),
                ]

                if (queue.length === 0) return

                queue.forEach(notification => {
                    showDispatcherNotification(notification)
                })

                store.commit('orders/SET_DISPATCHER_ORDER_NOTIFICATION', null)
            }
        )

        // Очистка при размонтировании
        onScopeDispose(() => {
            unwatchCanceled()
            unwatchChanged()
            unwatchDispatcher()
        })

        return {}
    },

    render() {
        return null
    },
}
</script>

<style lang="scss">
.Vue-Toastification__toast.order-cancel-notification {
    z-index: 20;
    width: 500px;
    padding: 0.5rem;
}

.Vue-Toastification__toast.order-change-notification {
    position: absolute;
    z-index: 9;
    opacity: 0;

    &:nth-child(1 of .order-change-notification) {
        position: relative;
        z-index: 10;
        opacity: 1;
        transition: all 0.5s ease;
    }
}

@media screen and (max-width: 769px) {
    .Vue-Toastification__toast.order-change-notification {
        left: 0;
        right: 0;
    }

    .Vue-Toastification__toast.order-cancel-notification {
        width: 480px;
        margin-bottom: 4px !important;
    }
}

@media screen and (max-width: 500px) {
    .Vue-Toastification__toast.order-cancel-notification {
        width: 100% !important;
    }
}
</style>
